import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="Navigation principale"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => scrollToSection('hero')}
            className="flex items-center gap-3 cursor-pointer bg-transparent border-0"
            aria-label="Retour à l'accueil"
          >
            <img 
              src="https://public.readdy.ai/ai/img_res/14b15668-92e5-497b-9cc3-0a8272650769.png" 
              alt="FenetrePro Bordeaux Logo" 
              className="h-8 w-auto"
              width="32"
              height="32"
            />
            <span className={`text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>FenetrePro Bordeaux</span>
          </button>

          <div className="hidden lg:flex items-center gap-8">
            <a href="/#services" className="text-gray-700 hover:text-teal transition-colors font-medium cursor-pointer">
              Services
            </a>
            <a href="/#about" className="text-gray-700 hover:text-teal transition-colors font-medium cursor-pointer">
              À Propos
            </a>
            <a href="/#portfolio" className="text-gray-700 hover:text-teal transition-colors font-medium cursor-pointer">
              Réalisations
            </a>
            <Link to="/blog" className="text-gray-700 hover:text-teal transition-colors font-medium cursor-pointer">
              Blog
            </Link>
            <a href="/#contact" className="text-gray-700 hover:text-teal transition-colors font-medium cursor-pointer">
              Contact
            </a>
          </div>

          <a
            href="tel:+33554542545"
            className="hidden lg:flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-6 py-3 rounded-full transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
            aria-label="Appeler pour un devis gratuit"
          >
            <i className="ri-phone-line" aria-hidden="true"></i>
            <span className="font-semibold">Devis Gratuit</span>
          </a>

          <button
            onClick={toggleMobileMenu}
            className={`lg:hidden text-2xl cursor-pointer bg-transparent border-0 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden mt-4 pb-4 flex flex-col gap-4 bg-white rounded-lg p-4 shadow-lg"
            role="menu"
          >
            <button onClick={() => scrollToSection('hero')} className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              Accueil
            </button>
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              Services
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              À Propos
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              FAQ
            </button>
            <Link to="/blog" className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              Blog
            </Link>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal transition-colors text-left cursor-pointer" role="menuitem">
              Contact
            </button>
            <a
              href="tel:+33554542545"
              className="flex items-center justify-center gap-2 bg-orange text-white px-6 py-3 rounded-full whitespace-nowrap cursor-pointer"
              aria-label="Appeler pour un devis gratuit"
            >
              <i className="ri-phone-line" aria-hidden="true"></i>
              <span className="font-semibold">Devis Gratuit</span>
            </a>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}